import React from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { CopyCheck, RotateCcw } from "lucide-react";

const SHADOWS = [["default", "Default"], ["none", "None"], ["sm", "Soft"], ["lg", "Deep"]];

export const childStyleOf = (k) => ({ hideHeader: !!k.config?.options?.hideHeader, ...(k.config?.options?.appearance || {}) });

// Patch for one child widget: title visibility + WidgetCard appearance (bg / border / radius / shadow / transparent).
export function childStylePatch(k, style) {
  const { hideHeader, ...ap } = style || {};
  const opts = k.config?.options || {};
  const appearance = { ...(opts.appearance || {}) };
  ["bg_color", "border_color", "border_width", "radius", "shadow", "transparent"].forEach((key) => {
    if (ap[key] === undefined) return;
    if (ap[key] === "" || ap[key] === null) delete appearance[key]; else appearance[key] = ap[key];
  });
  const options = { ...opts, appearance };
  if (hideHeader !== undefined) options.hideHeader = !!hideHeader;
  return { config: { ...(k.config || {}), options } };
}

// Item 2 hardening: per-child formatting inside a Combine container (expandable child row).
export function ContainerChildStyle({ child, onPatch, onApplyToAll, testid }) {
  const s = childStyleOf(child);
  const set = (p) => onPatch(childStylePatch(child, p));
  const Row = ({ label, children }) => (
    <div className="flex items-center justify-between gap-2">
      <Label className="text-[10px] text-[color:var(--ink-3)]">{label}</Label>
      {children}
    </div>
  );
  return (
    <div data-testid={testid} className="mt-1 mb-1 ml-5 rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
      <Row label="Show title"><Switch data-testid={`${testid}-title`} checked={!s.hideHeader} onCheckedChange={(v) => set({ hideHeader: !v })} /></Row>
      <Row label="Background">
        <span className="flex items-center gap-1">
          <input data-testid={`${testid}-bg`} type="color" value={s.bg_color || "#ffffff"} onChange={(e) => set({ bg_color: e.target.value, transparent: false })} className="h-6 w-8 rounded border border-[color:var(--glass-border)] bg-transparent p-0" aria-label="Child background colour" />
          <Switch data-testid={`${testid}-transparent`} checked={!!s.transparent} onCheckedChange={(v) => set({ transparent: v })} aria-label="Transparent background" />
          <span className="text-[9px] text-[color:var(--ink-3)]">clear</span>
        </span>
      </Row>
      <Row label="Border">
        <span className="flex items-center gap-1">
          <input data-testid={`${testid}-border-color`} type="color" value={s.border_color || "#e2e8f0"} onChange={(e) => set({ border_color: e.target.value })} className="h-6 w-8 rounded border border-[color:var(--glass-border)] bg-transparent p-0" aria-label="Child border colour" />
          <Input data-testid={`${testid}-border-width`} type="number" min={0} max={8} value={s.border_width ?? ""} placeholder="px" onChange={(e) => set({ border_width: e.target.value === "" ? "" : Number(e.target.value) })} className="h-6 w-14 text-[10px] bg-[color:var(--surface-2)]" aria-label="Child border width" />
        </span>
      </Row>
      <Row label="Radius">
        <Input data-testid={`${testid}-radius`} type="number" min={0} max={40} value={s.radius ?? ""} placeholder="px" onChange={(e) => set({ radius: e.target.value === "" ? "" : Number(e.target.value) })} className="h-6 w-14 text-[10px] bg-[color:var(--surface-2)]" aria-label="Child corner radius" />
      </Row>
      <Row label="Shadow">
        <Select value={s.shadow || "default"} onValueChange={(v) => set({ shadow: v })}>
          <SelectTrigger data-testid={`${testid}-shadow`} className="h-6 w-[90px] text-[10px] bg-[color:var(--surface-2)]"><SelectValue /></SelectTrigger>
          <SelectContent>{SHADOWS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      <div className="flex gap-1 pt-1">
        <Button data-testid={`${testid}-apply-all`} size="sm" variant="outline" className="h-6 text-[10px] flex-1 bg-[color:var(--surface-2)]" onClick={() => onApplyToAll(s)}>
          <CopyCheck className="w-3 h-3 mr-1" />Apply to all children
        </Button>
        <Button data-testid={`${testid}-reset`} size="sm" variant="outline" className="h-6 text-[10px] px-2 bg-[color:var(--surface-2)]" title="Reset child style"
          onClick={() => onPatch({ config: { ...(child.config || {}), options: { ...(child.config?.options || {}), appearance: {}, hideHeader: false } } })}>
          <RotateCcw className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}
